/**
 * Pin the page backdrop to the screen instead of the viewport.
 *
 * THE FAILURE
 *   Keyboard is configured `resize: 'native'`, so opening it shrinks the
 *   WKWebView's own frame — and that shrinks the layout viewport. The backdrop
 *   in index.css is `position: fixed; inset: 0`, i.e. exactly viewport-sized,
 *   so every keyboard open resized its compositor layer and forced a fresh
 *   raster of two full-screen radial gradients.
 *
 *   That is the half the 2026-08-12 fix missed. Promoting the backdrop to its
 *   own layer genuinely fixed scrolling, where the layer only ever MOVES — a
 *   compositor moves a layer for free. A keyboard open is a RESIZE, and no
 *   compositor resizes a layer for free: new dimensions mean a new raster.
 *
 *   So tapping a field still dropped frames, and because the WebView is
 *   deliberately non-opaque (MyViewController.swift sets isOpaque = false over
 *   a #0a0a0f root view, to kill the launch black-flash), the half-painted
 *   surface showed that near-black through the whole view — the screen
 *   appearing to darken for a beat rather than just a band at the edge.
 *
 * THE FIX
 *   Size the backdrop from `screen`, which a keyboard does not change. The
 *   layer's dimensions then stay constant across open and close, so its raster
 *   is never invalidated and the compositor has nothing to redo. Rotation is a
 *   real size change and does re-measure.
 *
 *   Native only. On the web the window is freely resizable and there is no
 *   soft keyboard shrinking it, so the viewport-sized backdrop is both correct
 *   and cheap there — the CSS falls back to 100vw/100vh when these custom
 *   properties are absent.
 */

import { getViewScale } from "./viewScale";

/** Injectable for tests; real callers pass nothing. */
export interface BackdropDeps {
  screen?: { width: number; height: number };
  root?: { style: Pick<CSSStyleDeclaration, "setProperty"> };
  addListener?: (type: string, fn: () => void) => void;
  removeListener?: (type: string, fn: () => void) => void;
  /**
   * Points per CSS px (viewScale.ts). `screen` reports device points; on a
   * Display Zoom phone the page is laid out at 390px and scaled down, so the
   * backdrop must be screen / scale CSS px to still cover the screen.
   */
  viewScale?: number;
}

export const BACKDROP_W = "--backdrop-w";
export const BACKDROP_H = "--backdrop-h";

/**
 * Write the screen dimensions onto the root element. Returns what it wrote, or
 * null when there is nothing sane to write — a zero or missing `screen` means
 * leaving the CSS fallback in place, which is the current behaviour and always
 * correct, just not as cheap.
 */
export function applyBackdropSize(deps: BackdropDeps = {}): { w: number; h: number } | null {
  const screen = deps.screen ?? (typeof window !== "undefined" ? window.screen : undefined);
  const root = deps.root ?? (typeof document !== "undefined" ? document.documentElement : undefined);
  if (!screen || !root) return null;

  const rawScale = deps.viewScale ?? getViewScale();
  const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
  const w = Math.round(screen.width / scale);
  const h = Math.round(screen.height / scale);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;

  try {
    root.style.setProperty(BACKDROP_W, `${w}px`);
    root.style.setProperty(BACKDROP_H, `${h}px`);
  } catch {
    /* A backdrop that stays viewport-sized is a slow paint, never a broken page. */
    return null;
  }
  return { w, h };
}

/**
 * Measure once and re-measure on rotation. Returns a teardown for tests and
 * for symmetry; the app itself keeps this for the process lifetime.
 *
 * Deliberately NOT listening to `resize`. A keyboard opening fires resize, and
 * re-measuring there is precisely the invalidation this exists to avoid.
 */
export function initBackdropViewport(deps: BackdropDeps = {}): () => void {
  applyBackdropSize(deps);

  const add = deps.addListener ?? ((t, fn) => window.addEventListener(t, fn));
  const remove = deps.removeListener ?? ((t, fn) => window.removeEventListener(t, fn));
  const onRotate = (): void => {
    applyBackdropSize(deps);
  };

  add("orientationchange", onRotate);
  return () => remove("orientationchange", onRotate);
}
